import React from "react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@shadcn-in-nx/ui/alert-dialog";

export function useConfirm() {
    const [open, setOpen] = React.useState(false);
    const [config, setConfig] = React.useState({
        title: "",
        description: "",
        confirmText: "Continue",
        cancelText: "Cancel",
        confirmButtonClassName: "",
        isDestructive: false,
        isLoading: false,
    });
    const [resolver, setResolver] = React.useState({
        resolve: null,
        reject: null,
    });

    const confirm = React.useCallback(
        ({
            title,
            description,
            confirmText,
            cancelText,
            confirmButtonClassName,
            isDestructive = false,
            isLoading = false,
        } = {}) =>
            new Promise((resolve, reject) => {
                setConfig({
                    title: title || "Are you sure?",
                    description: description || "This action cannot be undone.",
                    confirmText: confirmText || "Continue",
                    cancelText: cancelText || "Cancel",
                    confirmButtonClassName: confirmButtonClassName || "",
                    isDestructive,
                    isLoading,
                });
                setResolver({ resolve, reject });
                setOpen(true);
            }),
        []
    );

    const handleClose = React.useCallback(() => {
        setOpen(false);
        resolver.reject?.();
    }, [resolver]);

    const handleConfirm = React.useCallback(() => {
        setOpen(false);
        resolver.resolve?.();
    }, [resolver]);

    const ConfirmDialog = () => (
        <AlertDialog
            open={open}
            onOpenChange={(open) => !open && handleClose()}
        >
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{config.title}</AlertDialogTitle>
                    <AlertDialogDescription>
                        {config.description}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>{config.cancelText}</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={handleConfirm}
                        className={
                            config.confirmButtonClassName ||
                            (config.isDestructive
                                ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                : "")
                        }
                        disabled={config.isLoading}
                    >
                        {config.isLoading ? "Loading..." : config.confirmText}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );

    return [confirm, ConfirmDialog];
}
